import type { EntityAction, FireCMSContext } from "@firecms/core";
import { CircularProgress, SendIcon } from "@firecms/ui";
import { FirebaseError, getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useId, useSyncExternalStore } from "react";
import type { EventInvitation } from "../collections/eventInvitations";
import { getAdminT, type AdminTranslateFn } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import { tableOnlyEntityAction } from "./entityActionDefaults";

/** Invitation ids with a send in flight, and which row icon should spin. */
const sendingInvitationIds = new Set<string>();
const sendingIconIds = new Map<string, string>();
let unscopedSendCount = 0;
let armedIconId: string | null = null;
const sendingListeners = new Set<() => void>();

const subscribeSendingInvitations = (listener: () => void) => {
  sendingListeners.add(listener);
  return () => {
    sendingListeners.delete(listener);
  };
};

const publishSendingInvitations = () => {
  sendingListeners.forEach((listener) => listener());
};

const iconShouldSpin = (iconId: string) =>
  unscopedSendCount > 0 || sendingIconIds.has(iconId);

const SENDING_SNACKBAR_MS = 10 * 60 * 1000;

function SendEventInvitationEmailIcon() {
  const iconId = useId();
  const sending = useSyncExternalStore(
    subscribeSendingInvitations,
    () => iconShouldSpin(iconId),
    () => false
  );

  return (
    <span
      className="inline-flex items-center justify-center"
      onPointerDown={() => {
        armedIconId = iconId;
      }}
    >
      {sending ? <CircularProgress size="smallest" /> : <SendIcon />}
    </span>
  );
}

function sendingMessage(t: AdminTranslateFn, email: string) {
  return (
    <span className="inline-flex items-center gap-2">
      <CircularProgress size="smallest" />
      {t("sendEventInvitationEmail.sending", { email })}
    </span>
  );
}

const invitationSendKey = (
  accountId: string,
  eventId: string,
  invitationId: string
) => `${accountId}/${eventId}/${invitationId}`;

const beginSending = (key: string) => {
  const iconId = armedIconId;
  armedIconId = null;
  sendingInvitationIds.add(key);
  if (iconId) {
    sendingIconIds.set(iconId, key);
  } else {
    unscopedSendCount += 1;
  }
  publishSendingInvitations();
  return iconId;
};

const endSending = (key: string, iconId: string | null) => {
  sendingInvitationIds.delete(key);
  if (iconId) {
    if (sendingIconIds.get(iconId) === key) {
      sendingIconIds.delete(iconId);
    }
  } else {
    unscopedSendCount = Math.max(0, unscopedSendCount - 1);
  }
  publishSendingInvitations();
};

const openSendingSnackbar = (
  context: FireCMSContext,
  t: AdminTranslateFn,
  email: string
) => {
  context.snackbarController.open({
    type: "info",
    message: sendingMessage(t, email),
    autoHideDuration: SENDING_SNACKBAR_MS,
  });
};

const openResultSnackbar = (
  context: FireCMSContext,
  type: "success" | "warning" | "error",
  message: string
) => {
  if (sendingInvitationIds.size <= 1) {
    context.snackbarController.close();
  }
  context.snackbarController.open({ type, message });
};

const STAFF_INVITATION_ROLES = new Set<EventInvitation["role"]>([
  "manager",
  "operator",
  "presenter",
]);

type SendEventInvitationEmailResponse = {
  sent: boolean;
  reason?: "disabled";
};

const isRecentSendError = (error: unknown): boolean =>
  error instanceof FirebaseError &&
  error.code === "functions/failed-precondition" &&
  error.message.toLowerCase().includes("sent recently");

export function buildSendEventInvitationEmailEntityAction(
  accountId: string,
  eventId: string
): EntityAction<EventInvitation> {
  const t = getAdminT(getAdminUiLocale());

  return {
    key: "send-event-invitation-email",
    name: t("sendEventInvitationEmail.entityAction"),
    icon: <SendEventInvitationEmailIcon />,
    ...tableOnlyEntityAction,
    collapsed: false,
    isEnabled: ({ entity }) => {
      if (!entity) {
        return false;
      }
      return (
        entity.values.status === "pending" &&
        STAFF_INVITATION_ROLES.has(entity.values.role)
      );
    },
    onClick: async ({ entity, context }) => {
      if (!entity) {
        armedIconId = null;
        return;
      }

      const email = entity.values.email ?? "";
      const key = invitationSendKey(accountId, eventId, entity.id);
      if (sendingInvitationIds.has(key)) {
        armedIconId = null;
        return;
      }

      const iconId = beginSending(key);
      openSendingSnackbar(context, t, email);

      try {
        const functions = getFunctions(getApp());
        const callable = httpsCallable<
          { accountId: string; eventId: string; invitationId: string },
          SendEventInvitationEmailResponse
        >(functions, "sendEventInvitationEmail");
        const response = await callable({
          accountId,
          eventId,
          invitationId: entity.id,
        });
        if (response.data.reason === "disabled") {
          openResultSnackbar(
            context,
            "warning",
            t("sendEventInvitationEmail.disabled")
          );
          return;
        }
        openResultSnackbar(
          context,
          "success",
          t("sendEventInvitationEmail.success")
        );
      } catch (error: unknown) {
        const message = isRecentSendError(error)
          ? t("sendEventInvitationEmail.cooldown")
          : t("sendEventInvitationEmail.error", {
              message:
                error instanceof Error
                  ? error.message
                  : t("common.unknownError"),
            });
        openResultSnackbar(
          context,
          isRecentSendError(error) ? "warning" : "error",
          message
        );
        console.error(error);
      } finally {
        endSending(key, iconId);
      }
    },
  };
}
